import { AlertCircle, CheckCircle, Clock, Database,RefreshCw, Wifi, WifiOff, X } from 'lucide-react';
import { useEffect,useState } from 'react';

import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useSyncStatus } from '../services/offilineSyncService';

// ✅ COMPONENTE PRINCIPAL: Gerenciador de status offline (exportado como era antes)
export function OfflineStatusManager() {
  return (
    <>
      <OfflineIndicator />
      <OnlineToast />
      <SyncStatus />
    </>
  );
}

// ✅ COMPONENTE: Barra offline (melhorada)
export default function OfflineIndicator() {
  const isOnline = useOnlineStatus();
  const { pending, failed } = useSyncStatus();

  if (isOnline) return null;

  return (
    <div className="fixed top-2 left-1/2 transform -translate-x-1/2 bg-red-600 dark:bg-red-700 text-white px-4 py-2 rounded-lg text-xs font-ageo-regular z-50 shadow-lg border border-red-700 dark:border-red-800 max-w-sm">
      <div className="flex items-center justify-center gap-2">
        <WifiOff className="w-3 h-3" />
        <span>
          Offline
          {pending > 0 && ` • ${pending} pendente${pending !== 1 ? 's' : ''}`}
          {failed > 0 && ` • ${failed} erro${failed !== 1 ? 's' : ''}`}
        </span>
      </div>
    </div>
  );
}

// ✅ COMPONENTE: Toast quando reconecta (melhorado)
export function OnlineToast() {
  const isOnline = useOnlineStatus();
  const { isSyncing, pending } = useSyncStatus();
  const [showToast, setShowToast] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline && isOnline) {
      // Só mostra se estava offline antes
      setShowToast(true);
      setWasOffline(false);
      
      if (pending > 0) {
        setToastMessage(`Conexão restaurada! Sincronizando ${pending} item${pending !== 1 ? 's' : ''}...`);
      } else {
        setToastMessage('Conexão restaurada!');
      }
      
      const timer = setTimeout(() => setShowToast(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, pending]);

  // ✅ Atualizar mensagem se sincronização mudar
  useEffect(() => {
    if (showToast && isOnline) {
      if (isSyncing) {
        setToastMessage(`Sincronizando ${pending} item${pending !== 1 ? 's' : ''}...`);
      } else if (pending === 0) {
        setToastMessage('Sincronização concluída!');
      }
    }
  }, [isSyncing, pending, showToast, isOnline]);

  if (!showToast || !isOnline) return null;

  return (
    <div className="fixed top-4 right-4 bg-confirmation dark:bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg z-50 font-ageo-regular text-sm animate-slide-down border border-green-600 dark:border-green-500 max-w-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {isSyncing ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <CheckCircle className="w-4 h-4" />
          )}
          <div>
            <div className="font-ageo-bold">
              {isSyncing ? 'Sincronizando...' : 'Conexão restaurada!'}
            </div>
            <div className="text-xs text-white/90 dark:text-white/80">
              {toastMessage}
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowToast(false)}
          className="text-white/80 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ✅ COMPONENTE: Status de sincronização (melhorado)
export function SyncStatus() {
  const { pending, failed, isSyncing, isOnline } = useSyncStatus();
  const [showSync, setShowSync] = useState(false);
  const [lastSyncMessage, setLastSyncMessage] = useState('');

  useEffect(() => {
    if (pending > 0 || failed > 0 || isSyncing) {
      setShowSync(true);
      
      if (isSyncing) {
        setLastSyncMessage(`Sincronizando ${pending} item${pending !== 1 ? 's' : ''}...`);
      } else if (failed > 0) {
        setLastSyncMessage(`${failed} erro${failed !== 1 ? 's' : ''} de sincronização`);
      } else if (pending > 0) {
        setLastSyncMessage(`${pending} item${pending !== 1 ? 's' : ''} pendente${pending !== 1 ? 's' : ''}`);
      }
    } else {
      // Esconder após um delay quando terminar a sincronização
      const timer = setTimeout(() => {
        setShowSync(false);
        setLastSyncMessage('');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [pending, failed, isSyncing]);

  if (!showSync) return null;

  return (
    <div className={`fixed bottom-4 left-4 text-white px-3 py-2 rounded-lg shadow-lg z-50 font-ageo-regular text-sm border max-w-xs ${
      failed > 0 
        ? 'bg-red-600 dark:bg-red-700 border-red-500 dark:border-red-600'
        : isSyncing
        ? 'bg-blue-600 dark:bg-blue-700 border-blue-500 dark:border-blue-600'
        : 'bg-gray-600 dark:bg-gray-700 border-gray-500 dark:border-gray-600'
    }`}>
      <div className="flex items-center gap-2">
        {isSyncing ? (
          <RefreshCw className="w-3 h-3 animate-spin" />
        ) : failed > 0 ? (
          <AlertCircle className="w-3 h-3" />
        ) : pending > 0 ? (
          <Wifi className="w-3 h-3" />
        ) : (
          <CheckCircle className="w-3 h-3" />
        )}
        <span className="truncate">{lastSyncMessage || 'Sincronizado'}</span>
        {!isOnline && (
          <WifiOff className="w-3 h-3 text-white/70" />
        )}
      </div>
    </div>
  );
}

// ✅ NOVO: Componente para cards com indicador de cache
export function OfflineCard({ 
  children, 
  isFromCache = false,
  lastSync,
  onRefresh,
  className = ""
}: {
  children: React.ReactNode;
  isFromCache?: boolean;
  lastSync?: Date;
  onRefresh?: () => void;
  className?: string;
}) {
  const isOnline = useOnlineStatus();

  return (
    <div className={`relative ${className}`}>
      {children}
      
      {/* ✅ Indicador de cache */}
      {isFromCache && (
        <div className="absolute top-2 right-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-1 rounded text-xs font-ageo-regular flex items-center gap-1 border border-blue-200 dark:border-blue-800">
          <Database className="w-3 h-3" />
          <span>Cache</span>
          {isOnline && onRefresh && (
            <button
              onClick={onRefresh}
              className="ml-1 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              title="Atualizar dados"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* ✅ Timestamp do último sync */}
      {lastSync && (
        <div className="absolute bottom-2 right-2 text-xs text-gray-500 dark:text-gray-400 font-ageo-regular">
          Sync: {lastSync.toLocaleTimeString('pt-BR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      )}
    </div>
  );
}

// ✅ NOVO: Botão com suporte offline
export function OfflineButton({
  children,
  onClick,
  disabled = false,
  isOfflineAction = false,
  className = "",
  ...props
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  isOfflineAction?: boolean;
  className?: string;
  [key: string]: any;
}) {
  const isOnline = useOnlineStatus();
  const { pending } = useSyncStatus();

  const isDisabled = disabled || (!isOnline && !isOfflineAction);
  const showPendingIndicator = !isOnline && isOfflineAction && pending > 0;

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={`
        ${className}
        ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${showPendingIndicator ? 'relative' : ''}
        transition-all duration-200
      `}
      title={
        !isOnline && !isOfflineAction
          ? 'Ação não disponível offline'
          : !isOnline && isOfflineAction
          ? 'Ação será sincronizada quando conectar'
          : undefined
      }
      {...props}
    >
      {children}
      
      {/* ✅ Indicador de ação pendente */}
      {showPendingIndicator && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-pulse border border-white dark:border-gray-800"></div>
      )}
    </button>
  );
}

// ✅ NOVO: Hook para status offline completo
export function useOfflineStatus() {
  const isOnline = useOnlineStatus();
  const syncStatus = useSyncStatus();

  return {
    isOnline,
    ...syncStatus,
    hasUnsyncedChanges: syncStatus.pending > 0 || syncStatus.failed > 0,
    isFullyOffline: !isOnline,
    canPerformAction: (requiresOnline: boolean = false) => {
      return isOnline || !requiresOnline;
    }
  };
}

// ✅ NOVO: Componente de status detalhado do sistema
export function SystemOfflineStatus() {
  const { isOnline, pending, failed, isSyncing } = useOfflineStatus();
  const [cacheStats, setCacheStats] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const loadCacheStats = async () => {
      try {
        const { cacheService } = await import('../services/cacheService');
        const stats = await cacheService.getCacheStatus();
        setCacheStats(stats);
      } catch (error) {
        console.error('Erro ao carregar stats do cache:', error);
      }
    };

    if (!isOnline || showDetails) {
      loadCacheStats();
    }
  }, [isOnline, showDetails]);

  if (isOnline && pending === 0 && failed === 0 && !showDetails) {
    return (
      <button
        onClick={() => setShowDetails(true)}
        className="fixed bottom-4 right-4 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-2 shadow-lg z-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        title="Ver status do sistema"
      >
        <Database className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-sm font-ageo-regular max-w-xs z-30">
      <div className="space-y-2">
        {/* Header com botão de fechar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isOnline ? (
              <Wifi className="w-4 h-4 text-green-500" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-500" />
            )}
            <span className="font-ageo-bold">
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
          
          {showDetails && (
            <button
              onClick={() => setShowDetails(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Status da sincronização */}
        {(pending > 0 || failed > 0 || isSyncing) && (
          <div className="space-y-1">
            {isSyncing && (
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <RefreshCw className="w-3 h-3 animate-spin" />
                <span>Sincronizando...</span>
              </div>
            )}
            
            {pending > 0 && (
              <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-500">
                <Clock className="w-3 h-3" />
                <span>{pending} item{pending !== 1 ? 's' : ''} pendente{pending !== 1 ? 's' : ''}</span>
              </div>
            )}
            
            {failed > 0 && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-500">
                <AlertCircle className="w-3 h-3" />
                <span>{failed} erro{failed !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        )}

        {/* Estatísticas do cache */}
        {(!isOnline || showDetails) && cacheStats && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <div className="font-ageo-bold text-gray-700 dark:text-gray-300">Cache Local:</div>
            <div>📋 {cacheStats.tarefas} tarefas</div>
            <div>🏗️ {cacheStats.obras} obras</div>
            <div>💬 {cacheStats.comentarios} comentários</div>
            {cacheStats.documentosSize !== '0 Bytes' && (
              <div>📄 {cacheStats.documentos} docs ({cacheStats.documentosSize})</div>
            )}
            {cacheStats.pendingSync > 0 && (
              <div className="text-yellow-600 dark:text-yellow-400">
                🔄 {cacheStats.pendingSync} ações na fila
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ✅ CSS para animações (pode ser adicionado ao index.css)
export const offlineStyles = `
@keyframes slide-down {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-slide-down {
  animation: slide-down 0.3s ease-out;
}
`;